import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Card, CardContent } from '@material-ui/core';
import CardIcon from '../../../components/CardIcon';
import { AddIcon, RemoveIcon } from '../../../components/icons';
import IconButton from '../../../components/IconButton';
import AddButton from '../../../components/AddButton';
import Label from '../../../components/Label';
import Value from '../../../components/Value';
import useApprove2, { ApprovalState } from '../../../hooks/useApprove2';

import useModal from '../../../hooks/useModal';
import useStakedBalanceAutoCompounder from '../../../hooks/useStakedBalanceAutoCompounder';
import useTokenBalance from '../../../hooks/useTokenBalance';

import { getDisplayBalance } from '../../../utils/formatBalance';

import DepositModal from './DepositModal';
import WithdrawModal from './WithdrawModal';
import TokenSymbol from '../../../components/TokenSymbol';
import { AutoCompounder } from '../../../tomb-finance'
import CardBG from '../../../assets/img/CardBackground.png'
import CardBGHome2 from '../../../assets/img/CardBackground-Home2.png'
import useWithdrawAutocompounder from '../../../hooks/useWithdrawAutocompounder';
import useStakeAutocompounder from '../../../hooks/useStakeAutocompounder';
import useAgreeToTerms from '../../../hooks/useAgreeToTerms';
import useTombFinance from '../../../hooks/useTombFinance';
import CardBGHome3 from '../../../assets/img/CardBackground-3.png'
import useLpStats from '../../../hooks/useLpStats';
import CardPyramid from '../../../components/CardPyramid';
import ApproveModal from '../../Bank2/components/ApproveModal';
import InfoModal from './InfoModal';
import { FaInfoCircle } from 'react-icons/fa';
import TermsModal from './TermsModal';

const StyledCard = styled(CardPyramid)`
`;

interface StakeProps {
  autoCompounder: AutoCompounder;
  statsOnAutocompounder: any;
  autoCompounderLPStats: any;
}

const StyledCardContentInner = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
`;

const StyledCardWrapper = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  @media (max-width: 768px) {
    width: 95%;
  }
`;

const Stake: React.FC<StakeProps> = ({ statsOnAutocompounder, autoCompounder, autoCompounderLPStats }) => {
  const tombFinance = useTombFinance()
  const [approveStatus, approve] = useApprove2(autoCompounder.depositToken, autoCompounder.linkVault);
  const [isApproving, setIsApproving] = React.useState(false);
  const [agreeToTermsStatus, agreeToTerms] = useAgreeToTerms(tombFinance.contracts[autoCompounder.vaultContract]);

  const tokenBalance = useTokenBalance(autoCompounder.depositToken);
  // const tokenBalanceLPStaked = statsOnAutocompounder?.realLPsStaked
  const [stakedBalance, stakedBalanceDollars] = useStakedBalanceAutoCompounder(autoCompounder.vaultContract);
  
  // const stakedTokenPriceInDollars = useStakedTokenPriceInDollars(autoCompounder.depositTokenName, autoCompounder.depositToken); // Full price per share?
  const stakedTokenPriceInDollarsGetter = useLpStats(autoCompounder.depositTokenName);
  const stakedTokenPriceInDollars = stakedTokenPriceInDollarsGetter?.priceOfOne;
  // const stakedTokenPriceInDollars = autoCompounderLPStats ? autoCompounderLPStats.priceOfOne : '0.0000'
  
  const tokenPriceInDollars = useMemo(
    () => (stakedTokenPriceInDollars ? stakedTokenPriceInDollars : null),
    [stakedTokenPriceInDollars],
  );
  // const earnedInDollars = (
  //   Number(tokenPriceInDollars) * Number(getDisplayBalance(statsOnAutocompounder?.realLPsStaked, autoCompounder.depositToken.decimal))
  // ).toFixed(2);
  const { onStakeAutocompounder } = useStakeAutocompounder(autoCompounder);
  const { onWithdrawAutocompounder } = useWithdrawAutocompounder(autoCompounder);

  const [onPresentDeposit, onDismissDeposit] = useModal(
    <DepositModal
      max={tokenBalance}
      decimals={autoCompounder.depositToken.decimal}
      onConfirm={(amount) => {
        if (Number(amount) <= 0 || isNaN(Number(amount))) return;
        onStakeAutocompounder(amount);
        onDismissDeposit();
      }}
      tokenName={autoCompounder.depositTokenName}
      autoCompounder={autoCompounder}
    />,
  );

  const [onPresentWithdraw, onDismissWithdraw] = useModal(
    <WithdrawModal
      max={stakedBalance}
      decimals={autoCompounder.depositToken.decimal}
      onConfirm={(amount) => {
        if (Number(amount) <= 0 || isNaN(Number(amount))) return;
        onWithdrawAutocompounder(amount);
        onDismissWithdraw();
      }}
      tokenName={autoCompounder.depositTokenName}
    />,
  );

  const [onPresentApprove, onDismissApprove] = useModal(
    <ApproveModal
      max={tokenBalance}
      decimals={autoCompounder.depositToken.decimal}
      onConfirm={(amount) => {
        if (Number(amount) <= 0 || isNaN(Number(amount))) return;
        handleApprove(amount);
        onDismissApprove();
      }}
      tokenName={autoCompounder.depositTokenName}
    />,
  );

  const [onPresentInfo, onDismissInfo] = useModal(
    <InfoModal
      poolName={autoCompounder.depositTokenName}
      statsOnAutocompounder={statsOnAutocompounder}
      autoCompounderLPStats={autoCompounderLPStats}
      pairTokenName1={autoCompounder.depositTokenName.split('-')[0]}
      pairTokenName2={autoCompounder.depositTokenName.split('-')[1]}
      stakedTokenPriceInDollars1={tokenPriceInDollars}
      stakedTokenPriceInDollars2={tokenPriceInDollars}
      autoCompounder={autoCompounder}
      linkVault={autoCompounder.linkVault}
      linkCompounder={autoCompounder.linkCompounder}
    />
  );

  const handleAgreeToTerms = async () => {
    try {
      await agreeToTerms();
      setHasAgreedToTheSarcophagiTerms(true);
    } catch (error) {
      console.error('Error agreeing to terms:', error);
    }
  };

  const [onPresentTerms, onDismissTerms] = useModal(
    <TermsModal
      onAgree={handleAgreeToTerms}
      onDismiss={() => onDismissTerms()}
    />
  );

  const handleApprove = async (amount: string) => {
    try {
      setIsApproving(true);
      await approve(amount);
    } catch (error) {
      console.error('Approval error:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const [hasAgreedToTheSarcophagiTerms, setHasAgreedToTheSarcophagiTerms] = useState(false)

  useEffect(() => {
    setHasAgreedToTheSarcophagiTerms(agreeToTermsStatus)
  }, [agreeToTermsStatus])

  // Check if user has any staked balance
  const hasStakedBalance = useMemo(() => {
    return stakedBalance && !stakedBalance.isZero();
  }, [stakedBalance]);

  return (
    <StyledCardWrapper>
      <div style={{ opacity: "1", willChange: "auto", transform: "none" }}>
        <div style={{ backfaceVisibility: "hidden", transformStyle: "preserve-3d", willChange: "auto", transform: "none" }} tabIndex={0}>
          <div className="relative flex flex-col items-center p-4 rounded-xl border bg-card/60 backdrop-blur text-card-foreground pointer-events-auto transition-all duration-200 hover:shadow-lg group" style={{ width: '100%', minHeight: "320px" }}>
            <button
              onClick={onPresentInfo}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            >
              <FaInfoCircle size={24} />
            </button>
            <StyledCardContentInner>
              <StyledCardHeader>
                {autoCompounder.depositTokenName === 'HOG-OS-LP' ? (
                  <>
                    <CardIcon>
                      <TokenSymbol symbol="HOG" size={54} />
                      <TokenSymbol symbol="OS" size={54} />
                    </CardIcon>
                  </>
                ) : autoCompounder.depositTokenName === 'GHOG-OS-LP' ? (
                  <>
                    <CardIcon>
                      <TokenSymbol symbol="GHOG" size={54} />
                      <TokenSymbol symbol="OS" size={54} />
                    </CardIcon>
                  </>
                ) : (
                  <CardIcon>
                    <TokenSymbol symbol={autoCompounder.depositTokenName} size={54} />
                  </CardIcon>
                )}

                {statsOnAutocompounder ? (
                  <Value value={statsOnAutocompounder.realLPsStaked} />
                ) : (
                  <Value value='0.0000' />
                )}
                <span className="text-m-body text-transparent bg-clip-text bg-gradient-sonic-horizontal">≈ ${statsOnAutocompounder ? (Number(tokenPriceInDollars) * Number(statsOnAutocompounder.realLPsStaked)).toFixed(4) : '0.0000' }</span>
                <span className="text-m-body text-transparent bg-clip-text bg-gradient-sonic-horizontal">${autoCompounder.depositTokenName} Staked</span>
              </StyledCardHeader>
              <StyledCardActions>
                {hasStakedBalance ? (
                  // If user has staked balance, show deposit/withdraw buttons regardless of approval status
                  <>
                    <IconButton onClick={onPresentWithdraw}>
                      <RemoveIcon />
                    </IconButton>
                    <StyledActionSpacer />
                    <AddButton
                      disabled={autoCompounder.closedForStaking}
                      onClick={() => (autoCompounder.closedForStaking ? null : onPresentDeposit())}
                    >
                      <AddIcon />
                    </AddButton>
                  </>
                ) : (
                  // If no staked balance, follow the normal approval flow
                  approveStatus !== ApprovalState.APPROVED ? (
                    <button
                      disabled={
                        autoCompounder.closedForStaking ||
                        approveStatus === ApprovalState.PENDING ||
                        approveStatus === ApprovalState.UNKNOWN ||
                        isApproving
                      }
                      onClick={onPresentApprove}
                      className='tracking-normal inline-flex whitespace-nowrap backdrop-blur items-center justify-center gap-x-1 py-2 px-6 rounded-full font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 [&>svg]:stroke-[3px] text-button-foreground bg-button/[0.16] hover:bg-button/[0.24] transition focus-visible:ring-button text-body'
                      style={{ marginTop: '20px' }}
                    >
                      {isApproving ? 'Approving...' : `Approve ${autoCompounder.depositTokenName}`}
                    </button>
                  ) : hasAgreedToTheSarcophagiTerms === false ? (
                    <>
                      <button
                        onClick={onPresentTerms}
                        className='tracking-normal inline-flex whitespace-nowrap backdrop-blur items-center justify-center gap-x-1 py-2 px-6 rounded-full font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 [&>svg]:stroke-[3px] text-button-foreground bg-button/[0.16] hover:bg-button/[0.24] transition focus-visible:ring-button text-body'
                        style={{ marginTop: '20px' }}
                      >
                        {`Agree to Terms`}
                      </button>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={onPresentWithdraw}>
                        <RemoveIcon />
                      </IconButton>
                      <StyledActionSpacer />
                      <AddButton
                        disabled={autoCompounder.closedForStaking}
                        onClick={() => (autoCompounder.closedForStaking ? null : onPresentDeposit())}
                      >
                        <AddIcon />
                      </AddButton>
                    </>
                  )
                )}
              </StyledCardActions>
            </StyledCardContentInner>
          </div>
        </div>
      </div>
    </StyledCardWrapper>
  );
};

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;
const StyledCardActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 28px;
  width: 100%;
`;

const StyledActionSpacer = styled.div`
  height: ${(props) => props.theme.spacing[4]}px;
  width: ${(props) => props.theme.spacing[4]}px;
`;

export default Stake;
